/**
 * Страница визарда заказа СОУТ (`/orders/:id/sout`, гард company). Загружает каркас заказа
 * (OrderDetail) и анкету (SoutAnketa) и передаёт их в SoutWizard. Для submitted-заказа анкета
 * приходит с `canEdit:false` — визард открывается read-only. Не-СОУТ заказ — мягкая ошибка.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ordersApi, soutApi, type OrderDetail, type SoutAnketa } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { serviceLabel } from '@/lib/orders'
import { SoutWizard } from '@/sout/sout-wizard'
import { useAuth } from '@/app/auth-context'
import { AnketaCancelOrderButton, CancelledOrderAlert } from '@/orders/order-cancel'
import { OrderQuestionButton } from '@/questions/ask-question'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

export function OrderSoutWizardPage() {
  const navigate = useNavigate()
  const { refresh } = useAuth()
  // Анкета отправлена прямо сейчас — отменять уже со страницы заказа, не с экрана «Отправлено».
  const [sent, setSent] = useState(false)
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  // Возврат со страницы рабочего места (p3-sout-workplaces §4): `?step=3&line=<id>` —
  // открыть визард сразу на шаге «Рабочие места» и прокрутить список к нужной строке.
  const [searchParams] = useSearchParams()
  const stepParam = searchParams.get('step')
  const initialStep = stepParam !== null && stepParam !== '' ? Number(stepParam) : undefined
  const anchorLineId = searchParams.get('line') || undefined
  /**
   * §8, p3-structure-sout-training (design.md, доп. решение волны 4): предупреждение о неполном
   * bulk-добавлении выбранных мест «Спецоценки» (`sout-dashboard.tsx`, `createOrderWithSelection`)
   * — заказ уже создан, часть/все места могли не добавиться (rejected[] или сбой самого запроса).
   * Передаётся ОДИН раз через history state перехода.
   *
   * Opus-ревью волны 4 (HIGH): снимаем значение РОВНО ОДИН РАЗ при монтировании через ленивый
   * инициализатор `useState`, а не читаем `location.state` на каждом рендере. Причина —
   * `SoutWizard` сам держит текущий шаг в query (`?step=N`) через `setSearchParams(fn,
   * { replace: true })` при монтировании; `replace` — это `navigate(path, { replace: true })`
   * БЕЗ state, поэтому запись истории заменяется на новую УЖЕ БЕЗ `bulkAddWarning` — при
   * следующем рендере `location.state` был бы `null`, и алерт исчезал бы почти сразу после
   * появления. Захват в `useState` переживает эту замену записи истории.
   */
  const [bulkAddWarning] = useState(
    () => (location.state as { bulkAddWarning?: string } | null)?.bulkAddWarning,
  )
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [anketa, setAnketa] = useState<SoutAnketa | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    Promise.all([ordersApi.get(id, controller.signal), soutApi.getAnketa(id, controller.signal)])
      .then(([ord, ank]) => {
        if (controller.signal.aborted) return
        setOrder(ord)
        setAnketa(ank)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id])

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/orders')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К моим заказам
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !order || !anketa ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : order.service !== 'sout' ? (
        <Alert variant="danger">
          Это заказ услуги «{serviceLabel(order.service)}» — визард СОУТ к нему не применим.
        </Alert>
      ) : (
        <>
          <PageHead
            title="Заказ СОУТ"
            subtitle="Заполните анкету спецоценки по шагам — черновик сохраняется автоматически"
            actions={
              sent ? undefined : (
                <div className="flex flex-wrap justify-end gap-2">
                  {/* Вопрос по заказу (client-questions; ТЗ п.9.7): у заказа «На заполнении» и
                      «Требуются правки» анкета и есть страница заказа. */}
                  <OrderQuestionButton order={order} onCreated={() => void refresh()} />
                  <AnketaCancelOrderButton order={order} onCancelled={() => void refresh()} />
                </div>
              )
            }
          />
          {order.dataStatus === 'changes_requested' && order.returnComment ? (
            <Alert variant="info" title="Требуются правки" className="mb-4">
              {order.returnComment}
            </Alert>
          ) : null}
          {order.dataStatus === 'cancelled' ? (
            <CancelledOrderAlert orderId={order.id} className="mb-4" />
          ) : null}
          {bulkAddWarning ? (
            <Alert variant="danger" title="Не всё добавлено автоматически" className="mb-4">
              {bulkAddWarning}
            </Alert>
          ) : null}
          <SoutWizard
            order={order}
            anketa={anketa}
            initialStep={initialStep}
            anchorLineId={anchorLineId}
            onSubmitted={() => {
              setSent(true)
              void refresh()
            }}
          />
        </>
      )}
    </div>
  )
}
